import React, { useState, useEffect, useCallback } from 'react';
import { safeCodeService } from '../services/safe-code.service';
import { SafeCode } from '../types/safe-code.types';
import { useNotification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Modal } from '../components/UI/Modal';

export const SafeCrackingPage: React.FC = () => {
  const [safeCodes, setSafeCodes] = useState<SafeCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<SafeCode | null>(null);
  const [expandedPrizes, setExpandedPrizes] = useState<Set<number>>(new Set());
  const { success, error } = useNotification();
  const { language } = useLanguage();

  // Глобальный префикс для новых призов
  const [globalPrefix, setGlobalPrefix] = useState('');

  // Форма для нового приза
  const [newPrize, setNewPrize] = useState({
    code: '',
    prizeName: '',
  });

  // Форма для редактирования приза
  const [editForm, setEditForm] = useState({
    prefix: '',
    code: '',
    prizeName: '',
    isOpened: false,
  });

  // Форма для добавления кода доступа
  const [accessCodeForm, setAccessCodeForm] = useState<{ [key: number]: string }>({});

  const loadSafeCodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await safeCodeService.getAllSafeCodes();
      setSafeCodes(data);
    } catch (err) {
      error(
        language === 'ru' ? 'Ошибка' : 'Error',
        language === 'ru' ? 'Не удалось загрузить призы' : 'Failed to load prizes'
      );
    } finally {
      setLoading(false);
    }
  }, [error, language]);

  useEffect(() => {
    loadSafeCodes();
  }, [loadSafeCodes]);

  const handleCreatePrize = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!globalPrefix || !newPrize.code || !newPrize.prizeName) {
      error(
        language === 'ru' ? 'Ошибка валидации' : 'Validation Error',
        language === 'ru' ? 'Заполните все обязательные поля' : 'Fill in all required fields'
      );
      return;
    }

    if (!/^\d{3}$/.test(newPrize.code)) {
      error(
        language === 'ru' ? 'Ошибка валидации' : 'Validation Error',
        language === 'ru' ? 'Код сейфа должен состоять из 3 цифр' : 'Safe code must be 3 digits'
      );
      return;
    }

    try {
      await safeCodeService.createSafeCode({
        prefix: globalPrefix,
        code: newPrize.code,
        prizeName: newPrize.prizeName,
      });
      success(
        language === 'ru' ? 'Успех' : 'Success',
        language === 'ru' ? 'Приз успешно создан' : 'Prize created successfully'
      );
      setNewPrize({ code: '', prizeName: '' });
      setIsModalOpen(false);
      loadSafeCodes();
    } catch (err: any) {
      if (err.response?.status === 409) {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Такой код сейфа уже существует' : 'Safe code already exists'
        );
      } else {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Не удалось создать приз' : 'Failed to create prize'
        );
      }
    }
  };

  const handleOpenEdit = (prize: SafeCode) => {
    setEditingCode(prize);
    setEditForm({
      prefix: prize.prefix,
      code: prize.code,
      prizeName: prize.prizeName,
      isOpened: prize.isOpened,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdatePrize = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingCode) return;

    if (!editForm.prefix || !editForm.code || !editForm.prizeName) {
      error(
        language === 'ru' ? 'Ошибка валидации' : 'Validation Error',
        language === 'ru' ? 'Заполните все обязательные поля' : 'Fill in all required fields'
      );
      return;
    }

    if (!/^\d{3}$/.test(editForm.code)) {
      error(
        language === 'ru' ? 'Ошибка валидации' : 'Validation Error',
        language === 'ru' ? 'Код сейфа должен состоять из 3 цифр' : 'Safe code must be 3 digits'
      );
      return;
    }

    try {
      await safeCodeService.updateSafeCode(editingCode.id, {
        prefix: editForm.prefix,
        code: editForm.code,
        prizeName: editForm.prizeName,
        isOpened: editForm.isOpened,
      });
      success(
        language === 'ru' ? 'Успех' : 'Success',
        language === 'ru' ? 'Приз успешно обновлен' : 'Prize updated successfully'
      );
      setIsEditModalOpen(false);
      setEditingCode(null);
      loadSafeCodes();
    } catch (err: any) {
      if (err.response?.status === 409) {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Такой код сейфа уже существует' : 'Safe code already exists'
        );
      } else {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Не удалось обновить приз' : 'Failed to update prize'
        );
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(language === 'ru' ? 'Удалить этот приз?' : 'Delete this prize?')) {
      return;
    }

    try {
      await safeCodeService.deleteSafeCode(id);
      success(
        language === 'ru' ? 'Успех' : 'Success',
        language === 'ru' ? 'Приз удален' : 'Prize deleted'
      );
      loadSafeCodes();
    } catch (err) {
      error(
        language === 'ru' ? 'Ошибка' : 'Error',
        language === 'ru' ? 'Не удалось удалить приз' : 'Failed to delete prize'
      );
    }
  };

  const togglePrize = (prizeId: number) => {
    const newExpanded = new Set(expandedPrizes);
    if (newExpanded.has(prizeId)) {
      newExpanded.delete(prizeId);
    } else {
      newExpanded.add(prizeId);
    }
    setExpandedPrizes(newExpanded);
  };

  const handleAddAccessCode = async (prizeId: number) => {
    const orderLastDigits = accessCodeForm[prizeId] || '';

    if (!orderLastDigits || !/^\d{3}$/.test(orderLastDigits)) {
      error(
        language === 'ru' ? 'Ошибка валидации' : 'Validation Error',
        language === 'ru' ? 'Введите 3 последние цифры заказа' : 'Enter last 3 digits of order'
      );
      return;
    }

    try {
      await safeCodeService.createAccessCode(prizeId, { orderLastDigits });
      success(
        language === 'ru' ? 'Успех' : 'Success',
        language === 'ru' ? 'Код доступа добавлен' : 'Access code added'
      );
      setAccessCodeForm({ ...accessCodeForm, [prizeId]: '' });
      loadSafeCodes();
    } catch (err: any) {
      if (err.response?.status === 400) {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Приз уже получен' : 'Prize already received'
        );
      } else if (err.response?.status === 409) {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Такой код доступа уже существует' : 'Access code already exists'
        );
      } else {
        error(
          language === 'ru' ? 'Ошибка' : 'Error',
          language === 'ru' ? 'Не удалось добавить код доступа' : 'Failed to add access code'
        );
      }
    }
  };

  const handleDeleteAccessCode = async (accessCodeId: number) => {
    if (!window.confirm(language === 'ru' ? 'Удалить этот код доступа?' : 'Delete this access code?')) {
      return;
    }

    try {
      await safeCodeService.deleteAccessCode(accessCodeId);
      success(
        language === 'ru' ? 'Успех' : 'Success',
        language === 'ru' ? 'Код доступа удален' : 'Access code deleted'
      );
      loadSafeCodes();
    } catch (err) {
      error(
        language === 'ru' ? 'Ошибка' : 'Error',
        language === 'ru' ? 'Не удалось удалить код доступа' : 'Failed to delete access code'
      );
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="container-app py-8">
        <div className="text-center text-text-secondary">{language === 'ru' ? 'Загрузка...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="container-app py-8">
      {/* Заголовок */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">
          {language === 'ru' ? 'Управление призами' : 'Prize Management'}
        </h1>
        <p className="text-text-secondary">
          {language === 'ru' ? 'Создавайте призы и управляйте кодами доступа' : 'Create prizes and manage access codes'}
        </p>
      </div>

      {/* Префикс и кнопка добавления */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex-1 sm:max-w-md">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {language === 'ru' ? 'Префикс для новых призов' : 'Prefix for new prizes'}
          </label>
          <input
            type="text"
            value={globalPrefix}
            onChange={(e) => setGlobalPrefix(e.target.value.toUpperCase())}
            className="input w-full"
            placeholder={language === 'ru' ? 'Например: SHOPE' : 'Example: SHOPE'}
          />
        </div>
        <div className="sm:mt-6">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary w-full sm:w-auto whitespace-nowrap"
            disabled={!globalPrefix}
          >
            {language === 'ru' ? '+ Добавить приз' : '+ Add Prize'}
          </button>
        </div>
      </div>

      {/* Модальное окно создания приза */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewPrize({ code: '', prizeName: '' });
        }}
        title={language === 'ru' ? 'Создать новый приз' : 'Create New Prize'}
        size="md"
      >
        <form onSubmit={handleCreatePrize} className="space-y-4">
          <div className="p-3 bg-blue-50 rounded border border-blue-200">
            <div className="text-sm text-blue-800">
              <span className="font-medium">{language === 'ru' ? 'Префикс:' : 'Prefix:'}</span>{' '}
              <span className="font-mono font-bold">{globalPrefix}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {language === 'ru' ? 'Код сейфа (3 цифры) *' : 'Safe Code (3 digits) *'}
            </label>
            <input
              type="text"
              value={newPrize.code}
              onChange={(e) => setNewPrize({ ...newPrize, code: e.target.value })}
              className="input"
              placeholder="123"
              maxLength={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {language === 'ru' ? 'Название приза *' : 'Prize Name *'}
            </label>
            <input
              type="text"
              value={newPrize.prizeName}
              onChange={(e) => setNewPrize({ ...newPrize, prizeName: e.target.value })}
              className="input"
              placeholder={language === 'ru' ? 'Бесплатный кофе' : 'Free Coffee'}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setNewPrize({ code: '', prizeName: '' });
              }}
              className="btn btn-secondary"
            >
              {language === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
            <button type="submit" className="btn btn-primary">
              {language === 'ru' ? 'Создать' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Модальное окно редактирования */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCode(null);
        }}
        title={language === 'ru' ? 'Редактировать приз' : 'Edit Prize'}
        size="md"
      >
        <form onSubmit={handleUpdatePrize} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {language === 'ru' ? 'Префикс кода' : 'Code Prefix'}
            </label>
            <input
              type="text"
              value={editForm.prefix}
              onChange={(e) => setEditForm({ ...editForm, prefix: e.target.value.toUpperCase() })}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {language === 'ru' ? 'Код сейфа' : 'Safe Code'}
            </label>
            <input
              type="text"
              value={editForm.code}
              onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
              className="input"
              maxLength={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {language === 'ru' ? 'Название приза' : 'Prize Name'}
            </label>
            <input
              type="text"
              value={editForm.prizeName}
              onChange={(e) => setEditForm({ ...editForm, prizeName: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.isOpened}
                onChange={(e) => setEditForm({ ...editForm, isOpened: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-text-primary">
                {language === 'ru' ? 'Приз получен' : 'Prize received'}
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingCode(null);
              }}
              className="btn btn-secondary"
            >
              {language === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
            <button type="submit" className="btn btn-primary">
              {language === 'ru' ? 'Сохранить' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Список призов */}
      {safeCodes.length === 0 ? (
        <div className="card text-center py-12 text-text-secondary">
          {language === 'ru' ? 'Нет призов' : 'No prizes'}
        </div>
      ) : (
        <div className="space-y-4">
          {safeCodes.map((prize) => (
            <div key={prize.id} className="card">
              {/* Заголовок приза */}
              <div className="flex items-center justify-between p-4 border-b border-border-primary">
                <div className="flex items-center gap-4 flex-1">
                  <button
                    onClick={() => togglePrize(prize.id)}
                    className="text-text-primary hover:text-primary-600 transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 transition-transform ${expandedPrizes.has(prize.id) ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-text-primary">{prize.prizeName}</h3>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          prize.isOpened
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {prize.isOpened
                          ? language === 'ru' ? 'Получен' : 'Received'
                          : language === 'ru' ? 'Не получен' : 'Not Received'}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-text-secondary">
                      {language === 'ru' ? 'Префикс' : 'Prefix'}: <span className="font-mono font-medium">{prize.prefix}</span>
                      {' • '}
                      {language === 'ru' ? 'Код сейфа' : 'Safe code'}: <span className="font-mono font-medium">{prize.code}</span>
                      {' • '}
                      {language === 'ru' ? 'Кодов доступа' : 'Access codes'}: {prize.accessCodes?.length || 0}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenEdit(prize)}
                    className="btn-icon text-blue-600 hover:bg-blue-50"
                    title={language === 'ru' ? 'Редактировать' : 'Edit'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(prize.id)}
                    className="btn-icon text-red-600 hover:bg-red-50"
                    title={language === 'ru' ? 'Удалить' : 'Delete'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Раскрывающийся контент */}
              {expandedPrizes.has(prize.id) && (
                <div className="p-4 bg-surface-secondary">
                  {/* Форма добавления кода доступа */}
                  {!prize.isOpened && (
                    <div className="mb-4 p-3 bg-surface-primary rounded border border-border-primary">
                      <h4 className="text-sm font-medium text-text-primary mb-2">
                        {language === 'ru' ? 'Добавить код доступа' : 'Add Access Code'}
                      </h4>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={accessCodeForm[prize.id] || ''}
                          onChange={(e) => setAccessCodeForm({ ...accessCodeForm, [prize.id]: e.target.value })}
                          className="input flex-1"
                          placeholder={language === 'ru' ? '3 последние цифры заказа' : 'Last 3 digits of order'}
                          maxLength={3}
                        />
                        <button
                          onClick={() => handleAddAccessCode(prize.id)}
                          className="btn btn-primary whitespace-nowrap"
                        >
                          {language === 'ru' ? 'Добавить' : 'Add'}
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-text-tertiary">
                        {language === 'ru'
                          ? `Полный код будет: ${prize.prefix}XXX (где XXX - 3 цифры заказа)`
                          : `Full code will be: ${prize.prefix}XXX (where XXX are 3 order digits)`}
                      </p>
                    </div>
                  )}

                  {/* Список кодов доступа */}
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-2">
                      {language === 'ru' ? 'Коды доступа' : 'Access Codes'}
                    </h4>
                    {!prize.accessCodes || prize.accessCodes.length === 0 ? (
                      <p className="text-sm text-text-secondary">
                        {language === 'ru' ? 'Нет кодов доступа' : 'No access codes'}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {prize.accessCodes.map((accessCode) => (
                          <div
                            key={accessCode.id}
                            className="flex items-center justify-between p-2 bg-surface-primary rounded border border-border-primary"
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-bold text-primary-600">{accessCode.code}</span>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  accessCode.isUsed
                                    ? 'bg-gray-100 text-gray-700'
                                    : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {accessCode.isUsed
                                  ? language === 'ru' ? 'Использован' : 'Used'
                                  : language === 'ru' ? 'Доступен' : 'Available'}
                              </span>
                              <span className="text-xs text-text-tertiary">{formatDate(accessCode.createdAt)}</span>
                            </div>
                            <button
                              onClick={() => handleDeleteAccessCode(accessCode.id)}
                              className="btn-icon text-red-600 hover:bg-red-50"
                              title={language === 'ru' ? 'Удалить' : 'Delete'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
